<template>
    <div class="page-container">
        <div class="flex items-center justify-between mb-8">
            <div class="flex items-center gap-4">
                <div class="bg-red-600 p-3 rounded-2xl shadow-lg">
                    <LucideIcon :size="24" class="text-white">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </LucideIcon>
                </div>
                <div>
                    <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.weaponIssue.title') }}</h2>
                    <p class="section-subtitle text-slate-500">{{ $t('household.weaponIssue.subtitle') }}</p>
                </div>
            </div>
              <div class="flex items-center gap-3">
                <button
                  @click="showFilters = !showFilters"
                  class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-900 text-xs font-black uppercase tracking-widest transition-all active:scale-95"
                >
                  <LucideIcon :size="16">
                    <path d="M3 4h18"/><path d="M7 12h10"/><path d="M10 20h4"/>
                  </LucideIcon>
                  {{ $t('household.weaponIssue.filtersBtn') }}
                </button>

                <button
                  v-if="filterDateFrom || filterDateTo"
                  @click="clearFilters"
                  class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-600 text-xs font-black uppercase tracking-widest transition-all active:scale-95"
                  :title="$t('household.weaponIssue.clearBtnTitle')"
                >
                  ✕ {{ $t('household.weaponIssue.clearBtn') }}
                </button>

                <button
                  @click="openDialog"
                  class="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md active:scale-95"
                >
                  <LucideIcon :size="18" class="text-white">
                    <path d="M5 12h14"/><path d="M12 5v14"/>
                  </LucideIcon>
                  {{ $t('household.weaponIssue.issueBtn') }}
                </button>
              </div>
        </div>

            <div v-if="showFilters" class="filter-panel">
              <div class="filter-container">
                <div class="filter-group">
                  <label class="filter-label">{{ $t('household.weaponIssue.filterDateFrom') }}</label>
                  <DatePicker v-model="filterDateFrom" format="iso" class="filter-input" />
                </div>
                <div class="filter-group">
                  <label class="filter-label">{{ $t('household.weaponIssue.filterDateTo') }}</label>
                  <DatePicker v-model="filterDateTo" format="iso" class="filter-input" />
                </div>
                <div class="filter-actions">
                  <button
                    v-if="filterDateFrom || filterDateTo"
                    @click="clearFilters"
                    class="filter-btn filter-btn--clear"
                  >
                    ✕ {{ $t('household.weaponIssue.clearBtn') }}
                  </button>
                </div>
              </div>
              <div class="filter-info" v-if="filterDateFrom || filterDateTo">
                <span>{{ $t('household.weaponIssue.showing', { count: filteredWeaponIssues.length }) }}</span>
              </div>
            </div>
        
        <!-- Статы -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.weaponIssue.statIssued') }}</p>
                        <p class="text-3xl font-extrabold text-blue-600">{{ issuedWeapons.length }}</p>
                    </div>
                    <div class="bg-blue-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-blue-600">
                             <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <div class="flex items-center gap-2">
                            <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.weaponIssue.statReturned') }}</p>
                            <span
                                v-if="filterDateFrom || filterDateTo"
                                class="px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest"
                                :title="$t('household.weaponIssue.filterTitle')"
                            >
                                {{ $t('household.weaponIssue.filterActive') }}
                            </span>
                        </div>
                        <p class="text-3xl font-extrabold text-green-600">{{ returnedWeapons.length }}</p>
                        <div v-if="filterDateFrom || filterDateTo" class="mt-1 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            {{ $t('household.weaponIssue.periodLabel') }} {{ filterDateFrom || '...' }} — {{ filterDateTo || '...' }}
                        </div>
                    </div>
                    <div class="bg-green-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-green-600">
                             <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.weaponIssue.statOverdue') }}</p>
                        <p class="text-3xl font-extrabold text-red-600">{{ overdueWeapons.length }}</p>
                    </div>
                    <div class="bg-red-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-red-600">
                             <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>
        </div>

        <!-- Списки -->
        <div class="space-y-8">
            <template v-for="group in groups" :key="group.id">
                <div v-if="group.items.length > 0" class="statement-card overflow-hidden">
                    <div :class="[group.color, 'px-8 py-4']">
                        <h3 class="font-black text-white text-lg lowercase first-letter:uppercase">{{ group.label }} ({{ group.items.length }})</h3>
                    </div>
                    <div class="divide-y divide-slate-100 p-6">
                        <div v-for="item in group.items" :key="item.id" class="py-6 first:pt-2 last:pb-2">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <h4 class="font-black text-slate-900 text-lg leading-tight mb-3 uppercase tracking-tight">
                                        {{ getWeaponName(item.weapon) }}
                                    </h4>
                                    
                                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                                        <div>
                                            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.weaponIssue.responsibleLabel') }}</p>
                                            <p class="text-sm font-bold text-slate-900">{{ getUserName(item.user) }}</p>
                                        </div>
                                        <div>
                                            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.weaponIssue.purposeLabel') }}</p>
                                            <p class="text-sm font-bold text-slate-900">{{ item.purpose }}</p>
                                        </div>
                                        <div class="flex gap-6">
                                            <div>
                                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.weaponIssue.issuedLabel') }}</p>
                                                <p class="text-sm font-bold text-slate-900">{{ formatDate(item.issue_date) }}</p>
                                            </div>
                                            <div>
                                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.weaponIssue.plannedReturn') }}</p>
                                                <p class="text-sm font-bold text-slate-900">{{ formatDate(item.return_date) }}</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <p v-if="item.notes" class="mt-3 text-xs font-medium text-slate-400 italic">
                                        &ldquo;{{ item.notes }}&rdquo;
                                    </p>
                                </div>

                                <div class="text-right flex flex-col items-end gap-4 min-w-[140px]">
                                    <template v-if="item.status === 'issued' || item.status === 'overdue'">
                                        <div>
                                            <p :class="['text-lg font-black leading-none mb-1', getDaysLeftClass(item)]">
                                                {{ getDaysLeftLabel(item) }}
                                            </p>
                                            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ $t('household.weaponIssue.tillReturn') }}</p>
                                        </div>
                                        <button
                                            @click="openReturnDialog(item)"
                                            class="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-black text-[10px] tracking-widest uppercase hover:bg-blue-600 hover:text-white transition-all active:scale-95"
                                        >
                                            <LucideIcon :size="12">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                                            </LucideIcon>
                                            {{ $t('household.weaponIssue.acceptWeaponBtn') }}
                                        </button>
                                    </template>
                                    <div v-else-if="item.status === 'returned'" class="flex flex-col items-end">
                                        <span class="bg-green-50 text-green-600 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest mb-1">{{ $t('household.weaponIssue.returnedStatus') }}</span>
                                        <p class="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{{ formatDate(item.actual_return_date) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <div v-if="weaponIssues.length === 0" class="empty-state-card">
                <LucideIcon :size="48" class="empty-icon">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </LucideIcon>
                <p>{{ $t('household.weaponIssue.empty') }}</p>
                <button class="link-btn" @click="openDialog">{{ $t('household.weaponIssue.issueBtn') }}</button>
            </div>
        </div>

        <!-- Диалог выдачи оружия -->
        <Teleport to="body">
            <div v-if="isDialogOpen" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                <div class="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl scale-in">
                    <div class="flex items-center gap-4 mb-8">
                        <div class="bg-red-600 p-2.5 rounded-xl shadow-lg shadow-red-100">
                            <LucideIcon :size="20" class="text-white"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></LucideIcon>
                        </div>
                        <h3 class="text-xl font-black text-slate-900 leading-tight">
                            {{ $t('household.weaponIssue.dialogTitle') }}
                        </h3>
                    </div>

                    <form @submit.prevent="handleSubmit" class="space-y-6">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.weaponIssue.weaponLabel') }}</label>
                                <select v-model="formData.weaponId" required class="form-input w-full">
                                    <option value="">{{ $t('household.weaponIssue.chooseWeapon') }}</option>
                                    <option v-for="w in weapons" :key="w.id" :value="w.id">{{ w.name }}</option>
                                </select>
                            </div>

                            <div>
                                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.weaponIssue.receiverLabel') }}</label>
                                <select v-model="formData.userId" required class="form-input w-full">
                                    <option value="">{{ $t('household.weaponIssue.chooseUser') }}</option>
                                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.full_name }}{{ (typeof u.position === 'string' && u.position) ? (' (' + u.position + ')') : (u.position?.name ? (' (' + u.position.name + ')') : '') }}</option>
                                </select>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.weaponIssue.issueDateLabel') }}</label>
                                <DatePicker v-model="formData.issueDate" format="iso" class="form-input w-full" />
                            </div>
                            <div>
                                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.weaponIssue.returnDateLabel') }}</label>
                                <DatePicker v-model="formData.returnDate" format="iso" class="form-input w-full" />
                            </div>
                        </div>

                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.weaponIssue.purposeLabel2') }}</label>
                            <input type="text" v-model="formData.purpose" required :placeholder="$t('household.weaponIssue.purposePlaceholder')" class="form-input w-full" />
                        </div>

                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.weaponIssue.notesLabel') }}</label>
                            <textarea v-model="formData.notes" rows="2" :placeholder="$t('household.weaponIssue.notesPlaceholder')" class="form-input w-full"></textarea>
                        </div>

                        <div class="flex gap-4 pt-4">
                            <button type="submit" class="btn-primary-modal flex-1 active:scale-95">{{ $t('household.weaponIssue.submitBtn') }}</button>
                            <button type="button" @click="isDialogOpen = false" class="btn-secondary flex-1 active:scale-95">{{ $t('common.cancel') }}</button>
                        </div>
                    </form>
                </div>
            </div>
        </Teleport>

        <!-- Диалог подтверждения возврата оружия -->
        <Teleport to="body">
            <div v-if="isReturnConfirmOpen" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
                <div class="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl scale-in">
                    <!-- Иконка -->
                    <div class="flex justify-center mb-6">
                        <div class="bg-green-100 p-4 rounded-full">
                            <LucideIcon :size="32" class="text-green-600">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                            </LucideIcon>
                        </div>
                    </div>

                    <!-- Заголовок -->
                    <h3 class="text-2xl font-black text-slate-900 text-center mb-2">
                        {{ $t('household.weaponIssue.returnConfirmTitle') }}
                    </h3>

                    <!-- Описание -->
                    <p class="text-center text-slate-600 text-sm mb-2">
                        {{ $t('household.weaponIssue.returnWeaponLabel') }} <span class="font-black text-slate-900">{{ selectedReturnItem?.weapon_name }}</span>
                    </p>
                    <p class="text-center text-slate-500 text-xs mb-6">
                        {{ $t('household.weaponIssue.returnResponsibleLabel') }} <span class="font-bold text-slate-700">{{ selectedReturnItem?.user_name }}</span>
                    </p>

                    <!-- Дополнительно -->
                    <div class="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                        <p class="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-1">{{ $t('household.weaponIssue.returnDateLabel2') }}</p>
                        <p class="text-lg font-black text-amber-900">
                            {{ new Date().toLocaleDateString('ru-RU') }}
                        </p>
                    </div>

                    <!-- Кнопки -->
                    <div class="flex gap-3">
                        <button
                            @click="confirmReturn"
                            class="flex-1 px-4 py-3 bg-green-600 text-white font-black rounded-xl hover:bg-green-700 transition-all active:scale-95 uppercase text-sm tracking-widest"
                        >
                            {{ $t('common.confirm') }}
                        </button>
                        <button
                            @click="isReturnConfirmOpen = false"
                            class="flex-1 px-4 py-3 bg-slate-100 text-slate-700 font-black rounded-xl hover:bg-slate-200 transition-all active:scale-95 uppercase text-sm tracking-widest"
                        >
                            {{ $t('common.cancel') }}
                        </button>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue';
import DatePicker from '@/components/widgets/DatePicker.vue';
import { getWeaponIssues, createWeaponIssue, updateWeaponIssue } from '@/api/household_special';
import { getNomenclatures, getNomenclatureTypes } from '@/api/household_nomenclatures';
import { getUsers } from '@/api/user'

export default {
  name: 'HouseholdWeaponIssueView',
  components: { LucideIcon, DatePicker },
  inject: {
    setSidebarCollapsed: { default: null },
  },
  data() {
    return {
      weaponIssues: [],
      nomenclatures: [],
      nomenclatureTypes: [],
      users: [],
      isDialogOpen: false,
      isReturnConfirmOpen: false,
      selectedReturnItem: null,
      showFilters: false,
      filterDateFrom: null,
      filterDateTo: null,      
      formData: {
        weaponId: '',
        userId: '',
        issueDate: new Date().toISOString().split('T')[0],
        returnDate: '',
        purpose: '',
        notes: '',
      }
    };
  },
  computed: {
    weapons() {
      const weaponType = this.nomenclatureTypes.find(t => t.name === 'Оружие');
      if (!weaponType) return [];
      return this.nomenclatures.filter(n => (n.nomenclature_type || n.type) == weaponType.id);
    },
    filteredWeaponIssues() {
      return this.weaponIssues.filter(w => {
        if (this.filterDateFrom && w.issue_date < this.filterDateFrom) return false
        if (this.filterDateTo && w.issue_date > this.filterDateTo) return false
        return true
      })
    },
    issuedWeapons() { return this.filteredWeaponIssues.filter(w => w.status === 'issued') },
    returnedWeapons() { return this.filteredWeaponIssues.filter(w => w.status === 'returned') },
    overdueWeapons() { return this.filteredWeaponIssues.filter(w => w.status === 'overdue') },
    groups() {
      return [
        { id: 'issued', label: this.$t('household.weaponIssue.sectionIssued'), items: this.issuedWeapons, color: 'bg-blue-600' },
        { id: 'overdue', label: this.$t('household.weaponIssue.sectionOverdue'), items: this.overdueWeapons, color: 'bg-red-600' },
        { id: 'returned', label: this.$t('household.weaponIssue.sectionReturned'), items: this.returnedWeapons, color: 'bg-green-600' },
      ];
    }
  },
  async created() {
    await this.loadAllData();
  },
  methods: {
    async loadAllData() {
      try {
        // Загружаем всех пользователей
        const usersRes = await getUsers();
        console.log('Users response:', usersRes);
        
        // Обработка различных структур ответа API
        if (Array.isArray(usersRes)) {
          this.users = usersRes;
        } else if (usersRes?.data && Array.isArray(usersRes.data)) {
          this.users = usersRes.data;
        } else if (usersRes?.results && Array.isArray(usersRes.results)) {
          this.users = usersRes.results;
        } else {
          this.users = [];
        }
        
        console.log('Loaded users:', this.users);
      } catch (error) {
        console.error('Ошибка при загрузке пользователей:', error);
        this.users = [];
      }
      
      await this.loadData();
    },
    async loadData() {
      try {
        const [issueRes, nomRes, typeRes] = await Promise.all([
          getWeaponIssues(),
          getNomenclatures(),
          getNomenclatureTypes()
        ]);
        
        this.weaponIssues = Array.isArray(issueRes.data) ? issueRes.data : (issueRes.data?.results || []);
        this.nomenclatures = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
        this.nomenclatureTypes = Array.isArray(typeRes.data) ? typeRes.data : (typeRes.data?.results || []);
        
        console.log('Weapon issues:', this.weaponIssues);
        console.log('Nomenclatures:', this.nomenclatures);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      }
    },
    getWeaponName(weaponId) {
      const id = typeof weaponId === 'object' ? weaponId?.id : weaponId;
      return this.nomenclatures.find(n => n.id === id)?.name || this.$t('household.shared.notKnown');
    },
    getUserName(userId) {
      const id = typeof userId === 'object' ? userId?.id : userId;
      const user = this.users.find(u => u.id === id);
      return user?.full_name || this.$t('household.shared.notKnown');
    },
    formatDate(d) {
      if (!d) return '';
      try {
        const date = new Date(d + 'T00:00:00Z');
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('ru-RU');
      } catch (e) {
        return '';
      }
    },
    getDaysLeft(item) {
    if (!item.return_date) return 0;
    try {
        const [year, month, day] = item.return_date.split('-').map(Number);
        const returnDate = new Date(year, month - 1, day); 
        
        const today = new Date();
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        
        const diffTime = returnDate.getTime() - todayDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        console.log('[getDaysLeft]', {
        return_date: item.return_date,
        returnDate: returnDate.toLocaleDateString('ru-RU'),
        todayDate: todayDate.toLocaleDateString('ru-RU'),
        diffDays: diffDays
        });
        
        return diffDays;
    } catch (e) {
        console.error('Ошибка расчёта дней:', e, item.return_date);
        return 0;
    }
    },
    getDaysLeftClass(item) {
      const days = this.getDaysLeft(item);
      if (days < 0) return 'text-rose-600';
      if (days <= 3) return 'text-amber-600';
      return 'text-emerald-600';
    },
    getDaysLeftLabel(item) {
      const days = this.getDaysLeft(item);
      return days < 0
        ? this.$t('household.weaponIssue.overdueDays', { days: Math.abs(days) })
        : this.$t('household.weaponIssue.daysLeft', { days });
    },
    async handleSubmit() {
      try {
        await createWeaponIssue({
          weapon: parseInt(this.formData.weaponId),
          user: parseInt(this.formData.userId),
          issue_date: this.formData.issueDate,
          return_date: this.formData.returnDate,
          purpose: this.formData.purpose,
          notes: this.formData.notes,
          status: 'issued'
        });
        this.isDialogOpen = false;
        this.formData = {
          weaponId: '',
          userId: '',
          issueDate: new Date().toISOString().split('T')[0],
          returnDate: '',
          purpose: '',
          notes: '',
        };
        await this.loadData();
      } catch (error) {
        console.error('Ошибка при создании записи:', error);
      }
    },
    openDialog() {
      if (typeof this.setSidebarCollapsed === 'function') {
        this.setSidebarCollapsed(true);
      }
      this.isDialogOpen = true;
    },
    openReturnDialog(item) {
      this.selectedReturnItem = {
        id: item.id,
        weapon_name: this.getWeaponName(item.weapon),
        user_name: this.getUserName(item.user)
      };
      this.isReturnConfirmOpen = true;
    },
    clearFilters() {
      this.filterDateFrom = null
      this.filterDateTo = null
    },
    async confirmReturn() {
      if (this.selectedReturnItem) {
        try {
          await updateWeaponIssue(this.selectedReturnItem.id, {
            status: 'returned',
            actual_return_date: new Date().toISOString().split('T')[0]
          });
          this.isReturnConfirmOpen = false;
          this.selectedReturnItem = null;
          await this.loadData();
        } catch (error) {
          console.error('Ошибка при возврате оружия:', error);
        }
      }
    }
  }
};
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.statement-card {
    background: white;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.form-input {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    padding: 0.75rem 1rem;
    color: #1e293b;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.2s;
}

/* Prevent white-on-white date text when global `color-scheme: dark` is enabled */
.form-input[type="date"],
.filter-input[type="date"] {
  color-scheme: light;
}

.form-input[type="date"]::-webkit-datetime-edit,
.filter-input[type="date"]::-webkit-datetime-edit {
  color: #1e293b;
}

.form-input[type="date"]::-webkit-datetime-edit-fields-wrapper,
.filter-input[type="date"]::-webkit-datetime-edit-fields-wrapper {
  color: #1e293b;
}

.form-input:focus {
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    outline: none;
}

.scale-in { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

.empty-state-card { text-align: center; padding: 5rem 2rem; background: white; border: 1px dashed #e2e8f0; border-radius: 1.25rem; }
.empty-icon { color: #cbd5e1; margin-bottom: 1.5rem; }
.empty-state-card p { color: #64748b; font-size: 1.1rem; margin-bottom: 1.5rem; }
.link-btn { background: #eff6ff; border: none; color: #2563eb; padding: 0.5rem 1rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.link-btn:hover { background: #dbeafe; }

.btn-primary-modal {
    background: #3b82f6;
    border: none;
    border-radius: 1rem;
    padding: 0.875rem 1.5rem;
    color: white;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary-modal:hover {
    background: #2563eb;
}

.btn-secondary {
    background: #e2e8f0;
    border: none;
    border-radius: 1rem;
    padding: 0.875rem 1.5rem;
    color: #475569;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary:hover {
    background: #cbd5e1;
}
.filter-panel {
  background: white;
  border-radius: 1.25rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid #eef0f4;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.filter-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.filter-input {
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
  font-family: inherit;
}

.filter-input:focus {
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  outline: none;
}

.filter-actions {
  display: flex;
  gap: 0.75rem;
}

.filter-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.875rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.filter-btn--clear {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}

.filter-btn--clear:hover {
  background: #e2e8f0;
  color: #475569;
}

.filter-info {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  border-left: 4px solid #3b82f6;
  border-radius: 0.5rem;
  color: #1e40af;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Responsive fix */
@media (max-width: 768px) {
  .filter-panel {
    padding: 1rem;
  }
  
  .filter-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .filter-actions {
    width: 100%;
  }
  
  .filter-btn {
    flex: 1;
  }
}
</style>